import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';

// ─── Types ────────────────────────────────────────────────────────────────────

interface HandlerDiaryStat {
  handler_id: string;
  handler_name: string;
  open_count: number;
  past_due_count: number;
  way_past_due_count: number;
  closed_count: number;
}

interface ManagerStatsResponse {
  handlers: HandlerDiaryStat[];
  count: number;
  way_past_due_threshold_days: number;
  source: string;
  note?: string;
}

type SortKey = 'handler_name' | 'open_count' | 'past_due_count' | 'way_past_due_count' | 'closed_count';
type SortDir = 'asc' | 'desc';

// ─── Component ────────────────────────────────────────────────────────────────

export default function DiaryManagerStats() {
  const [data, setData] = useState<ManagerStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('way_past_due_count');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result: ManagerStatsResponse = await api.getDiaryManagerStats();
      setData(result);
    } catch (err: any) {
      setError(err?.message || 'Failed to load diary stats.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  }

  const handlers = data?.handlers ?? [];
  const sorted = [...handlers].sort((a, b) => {
    const aVal = a[sortKey];
    const bVal = b[sortKey];
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortDir === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
  });

  // Totals
  const totals = handlers.reduce(
    (acc, h) => ({
      open: acc.open + h.open_count,
      past_due: acc.past_due + h.past_due_count,
      way_past_due: acc.way_past_due + h.way_past_due_count,
      closed: acc.closed + h.closed_count,
    }),
    { open: 0, past_due: 0, way_past_due: 0, closed: 0 }
  );

  function wayPastDueBadge(count: number) {
    if (count > 5) return 'bg-red-100 text-red-700 font-bold';
    if (count > 0) return 'bg-amber-100 text-amber-700 font-semibold';
    return 'bg-green-100 text-green-700';
  }

  function pastDueBadge(count: number) {
    if (count > 5) return 'text-amber-600 font-semibold';
    if (count > 0) return 'text-amber-500';
    return 'text-slate-500';
  }

  function SortTh({ label, colKey, align = 'right' }: { label: string; colKey: SortKey; align?: 'left' | 'right' }) {
    const active = sortKey === colKey;
    return (
      <th
        className={`px-4 py-3 font-semibold text-slate-600 whitespace-nowrap cursor-pointer select-none hover:text-blue-700 transition-colors ${align === 'right' ? 'text-right' : 'text-left'}`}
        onClick={() => handleSort(colKey)}
      >
        <span className="inline-flex items-center gap-1">
          {label}
          <span className="text-slate-400 text-xs">
            {active ? (sortDir === 'asc' ? '\u25B2' : '\u25BC') : '\u21C5'}
          </span>
        </span>
      </th>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-slate-800">Diary Stats by Handler</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {data ? `${data.count} handlers` : 'Loading...'}
            {data?.way_past_due_threshold_days ? ` \u00B7 Way past due = ${data.way_past_due_threshold_days}+ days` : ''}
          </p>
        </div>
        {!loading && (
          <button
            onClick={fetchStats}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors"
          >
            Refresh
          </button>
        )}
      </div>

      {/* Summary cards */}
      {!loading && !error && data && (
        <div className="grid grid-cols-4 gap-px bg-slate-100">
          <div className="bg-white px-4 py-3 text-center">
            <p className="text-2xl font-bold text-blue-600 tabular-nums">{totals.open}</p>
            <p className="text-xs text-slate-500">Open</p>
          </div>
          <div className="bg-white px-4 py-3 text-center">
            <p className="text-2xl font-bold text-amber-500 tabular-nums">{totals.past_due}</p>
            <p className="text-xs text-slate-500">Past Due</p>
          </div>
          <div className="bg-white px-4 py-3 text-center">
            <p className={`text-2xl font-bold tabular-nums ${totals.way_past_due > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {totals.way_past_due}
            </p>
            <p className="text-xs text-slate-500">Way Past Due</p>
          </div>
          <div className="bg-white px-4 py-3 text-center">
            <p className="text-2xl font-bold text-slate-600 tabular-nums">{totals.closed}</p>
            <p className="text-xs text-slate-500">Closed</p>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <svg className="animate-spin w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            Loading diary stats...
          </div>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="px-6 py-8 text-center">
          <p className="text-sm text-red-600 font-medium">{error}</p>
          <button
            onClick={fetchStats}
            className="mt-3 px-4 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Table */}
      {!loading && !error && data && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <SortTh label="Handler" colKey="handler_name" align="left" />
                <SortTh label="Open" colKey="open_count" />
                <SortTh label="Past Due" colKey="past_due_count" />
                <SortTh label="Way Past Due" colKey="way_past_due_count" />
                <SortTh label="Closed" colKey="closed_count" />
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-slate-400">
                    No diary data available.
                  </td>
                </tr>
              ) : (
                sorted.map((h) => (
                  <tr
                    key={h.handler_id}
                    className="border-b border-slate-100 hover:bg-blue-50 transition-colors"
                  >
                    <td className="px-4 py-3 text-slate-800 font-medium whitespace-nowrap">
                      {h.handler_name || '\u2014'}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                      {h.open_count}
                    </td>
                    <td className={`px-4 py-3 text-right tabular-nums ${pastDueBadge(h.past_due_count)}`}>
                      {h.past_due_count}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-full text-xs tabular-nums ${wayPastDueBadge(h.way_past_due_count)}`}>
                        {h.way_past_due_count}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-500">
                      {h.closed_count}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Fallback notice */}
      {data?.note && (
        <div className="px-4 py-2 bg-amber-50 border-t border-amber-200 text-xs text-amber-700">
          {data.note}
        </div>
      )}
    </div>
  );
}
